// İSG Portal — ProgressIndicator (S33 planı Stage 2/5, yeni): dikey durum çizelgesi (permit
// yaşam döngüsü). Henüz hiçbir sayfa tüketmiyor — gerçek adım eşlemesi (Draft → Submitted →
// Review → Entry permit → Active, `Revision`'ın "current" adımın HATA hâli olarak, `Rejected`/
// `Expired`'ın uç kırmızı/gri adım olarak gösterilmesi) Slice 3'ün işi (plan §6 Slice 3). Bu
// primitif yalnız DÖRT adım DURUMUNU bilir — hangi backend durumunun hangi adıma/duruma
// düştüğü ÇAĞIRANIN kararıdır, component'te İŞ KURALI YOK.

import { Check } from 'lucide-react';
import type { ReactNode } from 'react';
import { cn } from './cn';

export type ProgressStepStatus = 'complete' | 'current' | 'error' | 'upcoming' | 'terminal';

export interface ProgressStep {
  label: ReactNode;
  status: ProgressStepStatus;
  /** 12px "aktör · tarih" satırı — yalnız GERÇEKTEN var olan bir etkinlik kaydından
   * geliyorsa verilir; hiçbir zaman uydurulmaz (plan §6 Slice 3 "omit unbacked data"). */
  meta?: ReactNode;
}

// Not: bu component `carbon:` KULLANMAZ — hiçbir class'ı Carbon'a ÖZEL değil (`--color-danger`
// gibi base'de zaten kayıtlı token'lar), bu yüzden 1b/1a'nın "yapısal fark → carbon: ek
// class" gerekçesi burada GEÇERSİZ: token DEĞERİ zaten her app'in kendi build'inde serbestçe
// çözülüyor (§2.0). Component henüz HİÇBİR yerde tüketilmediği için "bugünkü görünümü koru"
// yükümlülüğü de yok — brand-new primitif, korunacak eski bir davranış YOK.
const dot: Record<ProgressStepStatus, string> = {
  complete: 'bg-primary',
  current: 'border-2 border-primary bg-surface',
  error: 'border-2 border-danger bg-surface',
  upcoming: 'border border-border-strong bg-surface',
  // `terminal` — bir sürecin `error` (Rejected, kırmızı) GİBİ uca ULAŞTIĞI ama nötr olduğu
  // son adım (S33 Slice 7 QA fix — Expired: onay geçmişte verildi, geçerlilik penceresi
  // bitti). Dolu gri disk — `upcoming`'in İÇİ BOŞ ince halkasından KASITLI farklı ("buraya
  // varıldı" ile "henüz gelinmedi" TEK renkle değil dolgu/halka karşıtlığıyla ayrışır, a11y
  // kuralı "never let colour alone carry state").
  terminal: 'bg-border-strong',
};

const connector: Record<ProgressStepStatus, string> = {
  complete: 'bg-primary',
  current: 'bg-border',
  error: 'bg-border',
  upcoming: 'bg-border',
  terminal: 'bg-border',
};

const label: Record<ProgressStepStatus, string> = {
  complete: 'text-fg',
  current: 'text-fg font-semibold',
  error: 'text-danger font-semibold',
  upcoming: 'text-muted',
  // `text-fg` (`upcoming`'in soluk `text-muted`'ı DEĞİL) — bu adım GERÇEKLEŞTİ (yalnız nötr
  // sonuçla), "henüz gelinmedi" ile karıştırılmaması için okunur ağırlıkta.
  terminal: 'text-fg font-semibold',
};

export function ProgressIndicator({
  steps,
  className,
}: {
  steps: ProgressStep[];
  className?: string;
}) {
  return (
    <ol className={cn('flex flex-col', className)}>
      {steps.map((step, index) => {
        const last = index === steps.length - 1;
        return (
          // biome-ignore lint/suspicious/noArrayIndexKey: sabit yaşam döngüsü sırası, hiç yeniden sıralanmaz; `label` bir `ReactNode`, garanti primitif değil.
          <li key={index} className="flex gap-3">
            <div className="flex flex-col items-center">
              <span
                className={cn(
                  'flex h-4 w-4 shrink-0 items-center justify-center rounded-full',
                  dot[step.status],
                )}
              >
                {step.status === 'complete' ? (
                  <Check className="h-2.5 w-2.5 text-primary-foreground" aria-hidden="true" />
                ) : step.status === 'current' ? (
                  <span className="h-2 w-2 rounded-full bg-primary" aria-hidden="true" />
                ) : null}
              </span>
              {!last ? (
                <span className={cn('w-px flex-1', connector[step.status])} aria-hidden="true" />
              ) : null}
            </div>
            <div className={cn('min-w-0', last ? 'pb-0' : 'pb-[calc(52px-1rem)]')}>
              <p className={cn('text-sm', label[step.status])}>{step.label}</p>
              {step.meta ? <p className="mt-0.5 text-xs text-muted">{step.meta}</p> : null}
            </div>
          </li>
        );
      })}
    </ol>
  );
}
